// 介錯 (the-executioner) の形態別フォルダに「実在する」絵のファイルの一覧。
//   目的: 読み込み (src/sprites.ts loadVariant) が、無いコマを毎回取りに行って空振りするのを止める。
//     以前は 1 回の読み込みで 160 枚を取りに行き、65 枚 (刀 23・大筒 42) が空振りしていた。
//     空振りは 404 ではなく 200 の HTML (soft404・配布版で約 1.3KB) で返る。
//   ここに無い絵は取りに行かない。描く側は「未ロード」と同じ扱いになり、以前の空振り (画像として読めない) と
//     同じく pickDrawableKey が直前の絵 → 最後に描けた絵 → idle の順に代える。見た目は変わらない。
//   🔴 平置き (public/sprites/bosses/the-executioner/<key>.webp) へは戻さない。平置きの旧デザインは
//     2026-08-20 に _archived-2026-08-20-maul-olddesign/ へ退避済みで、戻すと別人が出る。
//   形式 = ファイル名の幹 (拡張子なし)。拡張子は SPRITE_EXT_BY_BOSS (介錯は webp)。
//   ⚠️ この一覧は手書き。public/ の実物と一致することは tests/sprite_variant_manifest.test.ts が縛る。
//     絵を足した・消した時はこの一覧も直す (直さないとテストが赤くなる)。
//   生成しない理由 = 約 115 行の短い一覧で、ビルドに手順や失敗点を足すより、テストで縛る手書きのほうが小さく済むため。
const VARIANT_SPRITE_FILE_STEMS: Record<string, readonly string[]> = {
  'the-executioner#blade': [
    'dash_travel',
    'defeat',
    'downswing_mid',
    'downswing_smear',
    'downswing_trail',
    'exec_contact',
    'exec_contact_v2',
    'exec_key_settle_1022',
    'exec_hold',
    'exec_parried_1021',
    'exec_windup',
    'fajin_coil',
    'fajin_cut',
    'fajin_draw',
    'fajin_fall',
    'fajin_finish',
    'fajin_high',
    'fajin_lift',
    'fajin_release',
    'fajin_return',
    'fajin_settle',
    'fajin_thrust',
    'grabbed_hold',
    'horizontal_slash',
    'idle',
    'jab_coil',
    'jab_lunge',
    'jab_return_1',
    'jab_return_2',
    'jab_sink_1',
    'kick_windup',
    'morph_a',
    'morph_b',
    'morph_c',
    'overhead_contact',
    'overhead_contact_wfix',
    'overhead_hang',
    'overhead_raise',
    'overhead_recover',
    'overhead_recover_wfix',
    'overhead_repull',
    'pierce_draw',
    'pierce_draw_b',
    'pierce_thrust',
    'pierce_thrust_b',
    'quake_contact',
    'quake_contact_wfix',
    'roar_pose',
    'rise1',
    'rise2',
    'scoop_drag',
    'scoop_lag',
    'scoop_lift',
    'scoop_smear',
    'scoop_through',
    'scoop_touch',
    'scoop_twist',
    'set_backstep',
    'set_place',
    'shove_contact',
    'slam_contact',
    'slam_hold',
    'slam_hold_wfix',
    'slam_windup',
    'slam_windup_wfix',
    'slash_followthrough',
    'staggered_enter',
    'staggered_hold',
    'step_cloak_drop',
    'step_cloak_drop_align',
    'step_cloak_fall',
    'step_cloak_fall_align',
    'step_cloak_lift',
    'step_cloak_lift_align',
    'step_crouch',
    'step_land',
    'step_settle',
    'step_settle_align',
    'step_smear',
    'sue_contact',
    'sue_flick',
    'sue_raise',
    'sweep_coil',
    'sweep_coil_wfix',
    'sweep_slash',
    'turn',
    'walk_contact_b',
    'walk_cross',
    'walk_passing',
    'walk_reach',
  ],
  'the-executioner#cannon': [
    'aim_hold',
    'cannon_fajin_cut',
    'cannon_fajin_finish',
    'cannon_fajin_land',
    'cannon_fajin_load',
    'cannon_fajin_step',
    'defeat',
    'downswing_mid',
    'downswing_smear',
    'downswing_smear_wfix',
    'downswing_trail',
    'exec_contact',
    'exec_contact_wfix',
    'exec_windup',
    'fire_recoil',
    'grabbed_hold',
    'idle',
    'kick_windup',
    'morph_a',
    'morph_b',
    'morph_c',
    'mortar_aim',
    'mortar_aim_wfix',
    'mortar_fire',
    'mortar_fire_wfix',
    'overhead_contact',
    'overhead_raise',
    'overhead_raise_wfix',
    'overhead_recover',
    'overhead_repull',
    'roar_pose',
    'rise1',
    'rise2',
    'set_place',
    'shove_contact',
    'slam_contact',
    'slam_hold',
    'slam_windup',
    'staggered_enter',
    'staggered_hold',
    'step_back',
    'step_cloak_drop',
    'step_cloak_fall',
    'step_cloak_lift',
    'step_crouch',
    'step_land',
    'step_settle',
    'step_smear',
    'sweep_contact',
    'sweep_windup',
    'sweep_windup_low_a',
    'sweep_windup_low_b',
    'sweep_windup_twist_a',
    'sweep_windup_twist_b',
    'turn',
    'volley_after',
    'walk_contact_b',
    'walk_cross',
    'walk_passing',
    'walk_reach',
  ],
}

const LISTED: Record<string, ReadonlySet<string>> = Object.fromEntries(
  Object.entries(VARIANT_SPRITE_FILE_STEMS).map(([id, stems]) => [id, new Set(stems)]),
)

/** 一覧を持つ形態か。持たない形態は従来どおり全部取りに行く。 */
export function hasVariantSpriteManifest(bossId: string, variant: string): boolean {
  return `${bossId}#${variant}` in LISTED
}

/** その形態のフォルダに、この幹のファイルが実在するか。一覧を持たない形態は常に true (従来どおり取りに行く)。 */
export function variantSpriteFileListed(bossId: string, variant: string, stem: string): boolean {
  const listed = LISTED[`${bossId}#${variant}`]
  return listed === undefined || listed.has(stem)
}

/** テスト用: 一覧の中身 (ファイル名の幹・並び順どおり)。 */
export function variantSpriteFileStems(bossId: string, variant: string): readonly string[] {
  return VARIANT_SPRITE_FILE_STEMS[`${bossId}#${variant}`] ?? []
}
